import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import NotificationModal from "@/components/modal/NotificationModal";
import API from "@/utils/api";

interface NotificationItem {
  id: string;
  type: string;
  message: string;
  timestamp: {
    _seconds: number;
    _nanoseconds: number;
  };
  isRead: boolean;
  displayTime: string;
  icon?: string;
}

interface NotificationProps {
  notifications?: { id: number; text: string; date: string }[];
  className?: string;
}

const Notification = ({ notifications = [], className = "" }: NotificationProps) => {
  const [showNotificationModal, setShowNotificationModal] = useState(false);

  // Fetch notifications from API
  const { data: apiNotifications } = useQuery<NotificationItem[]>({
    queryKey: ["notifications"],
    queryFn: async () => {
      const response = await API.get("notifications");
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      return response.data.data as NotificationItem[];
    },
    staleTime: 30000, // 30 seconds
    refetchOnWindowFocus: true,
  });

  // Map API notifications to the format expected by NotificationModal
  const mappedNotifications =
    apiNotifications?.map((notif, index) => ({
      id: index + 1,
      text: notif.message,
      date: notif.displayTime,
      icon: notif.icon,
    })) ?? [];

  // Use provided notifications if available, otherwise use API data, otherwise use empty array
  const displayNotifications = notifications.length > 0 ? notifications : mappedNotifications;

  // Check if there are any unread notifications
  const hasUnreadNotifications = apiNotifications?.some((notif) => !notif.isRead) ?? false;

  return (
    <div className={`relative ${className}`}>
      <div
        className="cursor-pointer"
        onClick={() => {
          setShowNotificationModal(!showNotificationModal);
        }}
      >
        <img src="/images/noti.svg" alt="Notification" />
        {hasUnreadNotifications && (
          <span className="absolute top-0 right-0 h-2 w-2 bg-red-500 rounded-xl"></span>
        )}
      </div>
      {showNotificationModal && (
        <NotificationModal
          notifications={displayNotifications}
          onClose={() => setShowNotificationModal(false)}
        />
      )}
    </div>
  );
};

export default Notification;
